import { LS_KEY_DARKMODE } from 'consts';
import { services } from 'services/base';
import { SelectedDarkMode } from 'types';
import { convertDarkModeStrToSelectedDarkMode, getMainConfig, registerStateServices } from 'utils';

type ThemeState = {
  /**
   * Whether dark mode is allowed by the configuration.
   *
   * The default value is coming from the theme, but it can be turned on/off
   * dynamically by `setDarkModeAllowed` service call.
   */
  darkModeAllowed: boolean;
  /**
   * Indicates whether dark mode is enabled or not.
   */
  darkMode: boolean;
  /**
   * It is used to store the dark mode selector state which is selected in the `DarkModeSelector`
   * component.
   *
   * Its default value is coming from `userStorage`. It it is not yet stored it is
   * defaulted to 'auto'.
   */
  darkModeSelection: SelectedDarkMode;
};

const SERVICE_PREFIX = 'appFrame.theme';

const initThemeServices = () => {
  const allowDarkModeFromConfig: boolean = getMainConfig('allowDarkMode') ?? false;
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const storedDarkModeSelection = convertDarkModeStrToSelectedDarkMode(
    services.appFrame.userStorage.getItem(LS_KEY_DARKMODE),
  );
  const darkModeDefault = storedDarkModeSelection === 'auto' ? mediaQuery.matches : storedDarkModeSelection;
  const state: ThemeState = {
    darkModeAllowed: allowDarkModeFromConfig,
    darkMode: darkModeDefault,
    darkModeSelection: storedDarkModeSelection,
  };

  registerStateServices({
    state,
    stateKey: 'darkMode',
    initValue: darkModeDefault,
    servicePrefix: SERVICE_PREFIX,
    serviceMethods: {
      get: 'isDarkMode',
      set: 'setDarkMode',
      onChange: 'onDarkModeChange',
    },
  });

  registerStateServices({
    state,
    stateKey: 'darkModeAllowed',
    initValue: allowDarkModeFromConfig,
    servicePrefix: SERVICE_PREFIX,
    serviceMethods: {
      get: 'isDarkModeAllowed',
      set: 'setDarkModeAllowed',
      onChange: 'onDarkModeAllowedChange',
    },
  });

  registerStateServices({
    state,
    stateKey: 'darkModeSelection',
    initValue: storedDarkModeSelection,
    servicePrefix: SERVICE_PREFIX,
    serviceMethods: {
      get: 'getDarkModeSelection',
      set: 'setDarkModeSelection',
      onChange: 'onDarkModeSelectionChange',
    },
  });

  services.appFrame.theme.onDarkModeSelectionChange((selectedDarkMode: SelectedDarkMode) => {
    services.appFrame.userStorage.setItem(LS_KEY_DARKMODE, `${selectedDarkMode}`);
    services.appFrame.theme.setDarkMode(selectedDarkMode === 'auto' ? mediaQuery.matches : selectedDarkMode === true);
  });

  mediaQuery.addEventListener('change', (event) => {
    if (services.appFrame.theme.getDarkModeSelection() === 'auto') {
      services.appFrame.theme.setDarkMode(event.matches);
    }
  });

  services.appFrame.userStorage.onUserStorageChange((newStorage) => {
    const darkMode = newStorage.getItem(LS_KEY_DARKMODE);
    if (!darkMode) {
      return;
    }

    const convertedDarkMode = convertDarkModeStrToSelectedDarkMode(darkMode);
    if (convertedDarkMode !== state.darkModeSelection) {
      services.appFrame.theme.setDarkModeSelection(convertedDarkMode);
    }
  });
};

export default initThemeServices;
